import { useFeatureFlag } from "#webapp/src/launch-darkly/useLaunchDarkly";
import { getTimeZoneGuess } from "@clockwise/client-commons/src/util/time-zone";
import { Link } from "@clockwise/design-system";
import { WarningFilled } from "@clockwise/icons";
import {
  EventContainer,
  NamedEvent,
} from "@clockwise/web-commons/src/components/feature-illustration";
import { FeatureSettingsTooltip } from "@clockwise/web-commons/src/components/feature-setting/FeatureSetting";
import { useGatewayQuery } from "@clockwise/web-commons/src/network/apollo/gateway-provider";
import { LoadingSpinner } from "@clockwise/web-commons/src/ui/LoadingSpinner";
import { DateTime, Interval } from "luxon";
import React from "react";
import { DisruptiveMeetingRow } from "./DisruptiveMeetingRow";
import { TeamSettingsSection } from "./TeamSettingsSection";
import { TeamMostDisruptiveMeetingsDocument } from "./__generated__/TeamMostDisruptiveMeetings.v2.generated";

interface TeamMostDisruptiveMeetingsProps {
  teamId: string;
}

const DisruptiveMeetingsIllustration = () => {
  return (
    <EventContainer>
      <NamedEvent
        duration={120}
        Icon={WarningFilled}
        startTime="FT killer"
        title="Team meeting"
        sentiment="warning"
      >
        <div className="cw-mt-4">
          <div>Andy -1h FT</div>
          <div>Raph -1.5h FT</div>
          <div>Luke -30m FT</div>
          <div>Eric -30m FT</div>
        </div>
      </NamedEvent>
    </EventContainer>
  );
};

export const TeamMostDisruptiveMeetings = ({ teamId }: TeamMostDisruptiveMeetingsProps) => {
  const [showDisruptiveMeetings, loadingFlag] = useFeatureFlag("TeamDisruptiveMeetings");
  const now = DateTime.now();
  const { data, loading } = useGatewayQuery(TeamMostDisruptiveMeetingsDocument, {
    variables: {
      teamId,
      end: now.endOf("week").toISODate(),
      start: now.startOf("week").toISODate(),
      timeZone: getTimeZoneGuess(),
    },
    errorPolicy: "all",
    skip: !showDisruptiveMeetings || loadingFlag,
  });

  if (!showDisruptiveMeetings || loadingFlag) {
    return null;
  }

  return (
    <TeamSettingsSection graphic={<DisruptiveMeetingsIllustration />}>
      <div cw-id="web-settings-disruptive-meetings" id="disruptive-meetings">
        <div className="cw-heading-2xl cw-mb-4">
          Optimize your team's{" "}
          <FeatureSettingsTooltip
            id="disruptive-meetings-desc-tooltip"
            title={
              <>
                Meetings that aren't flexible can fragment your calendar and disrupt your team's
                Focus Time. Mark meetings as flexible to protect your Focus Time.{" "}
                <Link
                  href="https://support.getclockwise.com/hc/en-us/articles/15992379131540-Clockwise-Setup-for-Managers"
                  target="_blank"
                  rel="noreferrer"
                  onEmphasis
                >
                  Learn more
                </Link>
              </>
            }
          >
            <span
              className="cw-underline cw-decoration-dashed cw-underline-offset-4 cw-decoration-1"
              aria-describedby="disruptive-meetings-desc-tooltip"
            >
              most disruptive meetings
            </span>
          </FeatureSettingsTooltip>
          ?
        </div>
        <div className="cw-body-lg cw-text-muted cw-mb-6">
          These meetings end up taking the most hours of your team and lead to the most fragmented
          time.
        </div>

        {data?.team && (
          <DisruptiveMeetingsTable meetings={data.team.disruptiveEvents} loading={loading} />
        )}
      </div>
    </TeamSettingsSection>
  );
};

interface DisruptiveMeetingsTableProps {
  loading: boolean;
  meetings: {
    id: string;
    externalEventId: string;
    title: string;
    eventPermissions: {
      canModify: boolean;
    };
    dateOrTimeRange:
      | { dateRange: string; __typename: "DateRange" }
      | { timeRange: string; __typename: "DateTimeRange" };
    attendees: {
      isOrganizer: boolean | null;
      person: {
        givenName?: string | null;
        familyName?: string | null;
        displayName: string;
        externalImageUrl?: string | null;
      };
    }[];
    recurrenceRule: string | null;
  }[];
}

const DisruptiveMeetingsTable = ({ meetings, loading }: DisruptiveMeetingsTableProps) => {
  if (loading) {
    return <LoadingSpinner />;
  }
  return (
    <div className="cw-border-y cw-border-solid cw-border-default cw-divide-y">
      {meetings.map((m) => (
        <DisruptiveMeetingRow
          key={m.id}
          eventId={m.externalEventId}
          title={m.title}
          start={
            Interval.fromISO(
              m.dateOrTimeRange.__typename === "DateRange"
                ? m.dateOrTimeRange.dateRange
                : m.dateOrTimeRange.timeRange,
            ).start
          }
          disruptedAttendees={m.attendees.map(({ person }) => person)}
          organizer={m.attendees.find(({ isOrganizer }) => isOrganizer)?.person}
          recurrence={m.recurrenceRule}
          canModify={m.eventPermissions.canModify}
        />
      ))}
    </div>
  );
};
